import { Component, Inject, Injectable, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TermsConditionsDialogComponent } from '@serviceplan/ui-kit';
import { AssetUIModel, ClientEnvironment, CoreDownloadService, ENVIRONMENT, FormatFileSizePipe, NestedTreeNode } from '@thehubz/client/core';
import {
  Asset,
  AssetHistoryApiService,
  AssetsApiService,
  BuyoutRights,
  CreateAssetHistoryDto,
  DownloadData,
  FileAccessApiService,
  ZipFileResponse,
} from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { filterFormats, setFormatName, sortFormats } from './asset-format';
import { AssetNestedTreeNode } from './asset-nested-tree-node.model';

interface DownloadAssetsDialogData {
  title: string;
  filename: string;
  keys?: string[];
  assetIds?: string[];
  assetDownloadHistory?: AnyType;
}

@Injectable({ providedIn: 'root' })
export class DownloadAssetsService {
  constructor(
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
    private dialog: MatDialog,
    private assetService: AssetsApiService,
    private formatFileSize: FormatFileSizePipe,
  ) {}

  async createDownloadData(assets: string[] | Asset[] | AssetUIModel[]): Promise<{ downloadData: AssetNestedTreeNode[]; assets: Asset[] }> {
    // check if assets is an array of asset ids or an array of assets
    const isStringOrHasNoCurrentVersion = (asset: string | Asset | AssetUIModel) => {
      if (typeof asset === 'string') {
        return true;
      }
      return (
        (asset as Asset).currentVersion === undefined || (asset as Asset).currentVersion === null || (asset as Asset).currentVersion?.aspectRatios === undefined
      );
    };

    let assetObjects: Asset[];
    if (assets.some((asset) => isStringOrHasNoCurrentVersion(asset))) {
      const assetIds = assets.map((ent) => (typeof ent === 'string' ? ent : ent._id.toString()));
      assetObjects = (await lastValueFrom(this.assetService.assetsControllerFindMany({ ids: assetIds, optFor: 'download' }))).result;
    } else {
      // need to deep copy the array to avoid changing the original array
      assetObjects = JSON.parse(JSON.stringify(assets)) as Asset[];
    }
    const downloadData = assetObjects.map((asset) => {
      // Extract the usageRights from the asset, filtering out any falsy values.
      const usageRights = asset.usageRights?.filter(Boolean);
      // Flatten the usageRights into a single array of buyoutRights,
      // then filter out any falsy values and those without an expiryDate,
      // and cast the result to an array of BuyoutRights.
      const flatBuyoutRights = usageRights?.flatMap((list) => list.buyoutRights).filter((list) => (list ? list.expiryDate : [])) as BuyoutRights[];

      const aspectRatios = asset.currentVersion?.aspectRatios;
      const children: NestedTreeNode[] =
        aspectRatios?.map((aspect) => {
          // transform aspect to nested tree node
          Object.defineProperty(aspect, 'name', {
            value: aspect.aspectRatioName ? $localize`:download popup:aspect` + ' ' + aspect.aspectRatioName : $localize`:download popup:default format`,
          });
          aspect.formats = aspect.formats?.filter(filterFormats(this.env)).sort(sortFormats(this.env)).map(setFormatName(this.formatFileSize));
          Object.assign(aspect, { children: aspect.formats });
          delete aspect['formats'];
          return aspect as unknown as NestedTreeNode;
        }) ?? [];

      return {
        assetId: asset._id,
        name: asset.name,
        version: asset.currentVersion?.version || 0,
        children: children,
        expiryDate: asset.expiryDate,
        licenseRegions: this.fetchLicenseRegion(flatBuyoutRights),
      };
    });
    return { downloadData, assets: assetObjects };
  }

  /**
   * This method, takes an array of BuyoutRights objects as an argument.
   * It returns a new array of BuyoutRights objects, where each object has a unique licenseRegion,
   * If there are no unique licenseRegions, the method returns undefined.
   * @param buyoutRights
   * @returns
   */
  fetchLicenseRegion(buyoutRights: BuyoutRights[]): BuyoutRights[] | undefined {
    // Create a new Map object to store the BuyoutRights objects, using the licenseRegion as the key.
    const buyoutRightsMap = new Map<string, BuyoutRights>();
    // Iterate over the input array of BuyoutRights objects.
    buyoutRights?.forEach((right) => {
      if (right) {
        // Get the current expiryDate from the BuyoutRights object.
        const currentExpiryDate = right.expiryDate || '';
        // Check if the Map already contains an entry for the licenseRegion.
        if (buyoutRightsMap.has(right.licenseRegion)) {
          // If it does, get the existing BuyoutRights object for that licenseRegion.
          const existingBuyoutRights = buyoutRightsMap.get(right.licenseRegion) as BuyoutRights;
          const existingExpiryDate = existingBuyoutRights?.expiryDate || '';
          // Compare the existing expiryDate with the current expiryDate.
          if (new Date(existingExpiryDate).getTime() < new Date(currentExpiryDate).getTime()) {
            // If the current expiryDate is later, update the expiryDate in the existing BuyoutRights object.
            existingBuyoutRights.expiryDate = currentExpiryDate;
          }
        } else {
          // If the Map does not contain an entry for the licenseRegion, add a new entry with the BuyoutRights object.
          buyoutRightsMap.set(right?.licenseRegion || '', { licenseRegion: right.licenseRegion, expiryDate: right.expiryDate });
        }
      }
    });
    // If the Map contains any entries, convert the values to an array and return it.
    // If the Map is empty, return undefined.
    return buyoutRightsMap.size ? Array.from(buyoutRightsMap.values()) : undefined;
  }

  /**
   * Bulk download files as zip. The keys are the paths in the bucket.
   *
   * The key can be created by using the util function `createAssetS3Key` from `@thehubz/shared/model`.
   * Examples of keys for an asset:
   * assets/634015a090e03415343c95dd/00001/aspect-default/main.mp4
   * assets/634015a090e03415343c95dd/00001/aspect-default/gen-preview.mp4
   * assets/634015a090e03415343c95dd/00001/aspect-default/gen-thumbnail.0000000.jpg
   *
   * @param keys Array of keys
   * @param filename Zip filename
   * @param title Dialog title
   * @returns MatDialogRef<DownloadAssetsDialogComponent, DownloadAssetsDialogData>
   */
  downloadFiles(keys: string[], filename: string, title = $localize`Download files`, assetDownloadHistory?: CreateAssetHistoryDto[]) {
    return this.dialog.open<DownloadAssetsDialogComponent, DownloadAssetsDialogData>(DownloadAssetsDialogComponent, {
      data: { title, filename, keys, assetDownloadHistory },
      disableClose: true,
      width: '500px',
      maxHeight: '550px',
    });
  }

  /**
   * Bulk download assets as zip. Only the main file is downloaded, not the thumbnails, previews, etc.
   *
   * @param assetIds Array of asset ids
   * @param filename Zip filename
   * @param title Dialog title
   * @returns MatDialogRef<DownloadAssetsDialogComponent, DownloadAssetsDialogData>
   */
  async downloadAssets(assetIds: string[], filename: string, title = $localize`Download assets`) {
    if (this.env.termsConditionsPopup?.onAssetDownload) {
      const dialogRef = this.dialog.open(TermsConditionsDialogComponent, {
        minHeight: '40rem',
        maxWidth: '40rem',
        data: {
          featureKey: 'assetDownload',
        },
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (!result.accepted) {
        return;
      }
    }

    return this.dialog.open<DownloadAssetsDialogComponent, DownloadAssetsDialogData>(DownloadAssetsDialogComponent, {
      data: { title, filename, assetIds },
      disableClose: true,
      width: '40rem',
    });
  }
}

@Component({
  template: `
    <div class="modal-body">
      <header class="modal-header flex justify-between">
        <h2 class="modal-headline" style="--modal-headline-font-size: 1.5rem"><ng-container i18n>Download assets</ng-container>: {{ data.title }}</h2>

        <button *ngIf="!prepareDownload" class="button-close ml-auto" mat-icon-button mat-dialog-close>
          <mat-icon class="material-icons-outlined" aria-hidden="true">close</mat-icon>
          <span class="sr-only" i18n>Close</span>
        </button>
      </header>

      <div class="modal-main">
        <div *ngIf="prepareDownload" class="pt-12 pb-12">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <p i18n class="mt-2">Preparing download...</p>
        </div>

        <div *ngIf="downloads" class="flex flex-col pb-4">
          <p class="mb-6" i18n>Download contains multiple files</p>

          <ol class="flex flex-col items-center gap-4">
            <li *ngFor="let download of downloads">
              <button class="button-primary" (click)="downloadUrl(download.downloadUrl, download.name, $event)">
                <mat-icon *ngIf="successfulDownloads.includes(download.downloadUrl)" class="material-icons-outlined" aria-hidden="true">check</mat-icon>
                <span>{{ download.name }} | {{ download.size | formatFileSize }}</span>
              </button>
            </li>
          </ol>
        </div>
      </div>
    </div>
  `,
  styles: [],
})
export class DownloadAssetsDialogComponent implements OnInit {
  prepareDownload = true;
  downloads?: ZipFileResponse[];
  successfulDownloads: string[] = [];

  constructor(
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
    @Inject(MAT_DIALOG_DATA) public data: DownloadAssetsDialogData,
    private dialogRef: MatDialogRef<DownloadAssetsDialogComponent>,
    private fileAccessService: FileAccessApiService,
    private coreDownloadService: CoreDownloadService,
    private assetHistoryApiService: AssetHistoryApiService,
  ) {}

  ngOnInit(): void {
    if (Array.isArray(this.data.keys) && this.data.keys.length > 0) {
      if (this.data.keys.length > 1) {
        this.fileAccessService.apiFileAccessControllerDownloadZip({ keys: this.data.keys, assetIds: this.data.assetIds }).subscribe({
          next: (res) => {
            this.pollDownloadProgress(res.downloadKey);
          },
          error: (err) => {
            console.error(err);
            this.dialogRef.close();
          },
        });
      }
      // just a single file, we can download it directly
      else {
        this.fileAccessService.apiFileAccessControllerGetPresignedUrl(this.data.keys[0], this.env.s3Config.bucket).subscribe((res) => {
          this.downloadUrl(res.presignedUrl, this.data.filename);
          if (this.data.assetDownloadHistory?.length) {
            this.updateDownloadHistory();
            this.assetHistoryApiService.assetHistoryControllerCreateManyDownloadHistory(this.data.assetDownloadHistory).subscribe();
          }
          this.dialogRef.close();
        });
      }
    }
  }

  private pollDownloadProgress(downloadKey: string) {
    const interval = setInterval(() => {
      this.fileAccessService.apiFileAccessControllerDownloadProgress(downloadKey).subscribe({
        next: (res) => {
          if (res.state === 'ready' && Array.isArray(res.response)) {
            clearInterval(interval);
            this.handleZipDownload(res);
          } else if (res.state === 'error') {
            console.error('error response from download progress');
            clearInterval(interval);
            this.dialogRef.close();
          }
        },
        error: (err) => {
          console.error(err);
          clearInterval(interval);
          this.dialogRef.close();
        },
      });
    }, 5000);
  }

  private handleZipDownload(download: DownloadData) {
    if (download.response?.length === 1) {
      this.downloadUrl(download.response[0].downloadUrl, this.data.filename); // download single file
      if (this.data.assetDownloadHistory?.length) {
        this.updateDownloadHistory();
        this.assetHistoryApiService.assetHistoryControllerCreateManyDownloadHistory(this.data.assetDownloadHistory).subscribe();
      }
      this.dialogRef.close();
    } else {
      const baseName = this.data.filename.split('.zip')[0];
      this.downloads = download.response?.map((file, idx) => {
        return {
          name: `${baseName}_${idx + 1}.zip`,
          downloadUrl: file.downloadUrl,
          size: file.size,
        };
      });
      this.prepareDownload = false;
    }
  }

  private updateDownloadHistory() {
    const assetDownloadHistory: AnyType[] = [];
    this.data.keys?.forEach((key: string) => {
      const assetKeyObject: string[] = key.split('/');
      const assetId = assetKeyObject[1];
      const fileFormatType =
        assetKeyObject[4] && assetKeyObject[4].split('.')[0] === 'main'
          ? 'ORIGINAL'
          : assetKeyObject[4]
            ? assetKeyObject[4].split('.')[0].toLocaleUpperCase()
            : '';
      const idx = this.data.assetDownloadHistory.findIndex((historyObject: AnyType) => historyObject.downloadedAsset === assetId);
      if (idx != -1) {
        const historyData: AnyType = JSON.parse(JSON.stringify(this.data.assetDownloadHistory[idx]));
        historyData.format = fileFormatType;
        assetDownloadHistory.push(historyData);
      }
    });
    this.data.assetDownloadHistory = assetDownloadHistory;
  }

  async downloadUrl(url: string, filename: string, event?: AnyType) {
    try {
      if (event && event.currentTarget) {
        event.currentTarget.disabled = true;
      }

      this.coreDownloadService.downloadSignedUrl(url, filename);
      this.successfulDownloads.push(url);
      if (this.downloads && this.successfulDownloads.length === this.downloads.length) {
        this.dialogRef.close();
      }
    } catch (error) {
      if (event && event.currentTarget) {
        event.currentTarget.disabled = false;
      }
    }
  }
}
